import type { ReactNode } from 'react';
import { CategoryTable } from './CategoryTable';
import { StepsSubphases } from './StepsSubphases';
import { MaterialsPhase, ModelsPhase } from './ModelsPhase';
import { PartsPhase } from './PartsPhase';
import { AssemblySubphases } from './AssemblySubphases';
import { PublishStep } from './PublishStep';

// What the pipeline is made of, as a LIST rather than as six hand-written sections.
// Adding, removing or reordering a phase is an edit to the array at the bottom of this
// file; the viewer renders whatever it is given and numbers it by position.
//
// THERE IS ONE PIPELINE. There were two — the manual read the same way either side, and
// then either an assembly clip generated per sub-step, or a mesh per part — and the list
// is what survived the other one being removed. Kept as a list, and not folded back into
// the viewer, for the reason it was made one: a phase is a line here, and the numbering,
// the progress bar and the tab ids all follow from the order rather than from six places
// agreeing about it.

// Everything a phase needs to render itself. The viewer owns all of it; a phase reads.
// THE MODELS PHASE'S TAB, the one phase a folder has (see PhaseCtx.folder).
export const MODELS_TAB = 'notice-step-7';

export type PhaseCtx = {
  docId: string;
  file: Blob;
  name: string;
  numPages: number;
  // A run-all session is in flight (locked) and whether it is moving (held is a paused
  // run — waiting, as against waiting forever).
  locked: boolean;
  held: boolean;
  // Whether THIS phase is the open tab. Only Publish asks: it does work on becoming
  // visible rather than on mount.
  active: boolean;
  // THE MANUAL ITSELF — the PDF, its drop zone and the product fields — built by the viewer, which owns the
  // file, the page count and the drag state. Phase 1 shows it in its Manual subphase; no
  // other phase reads it.
  manual: ReactNode;
  // A FOLDER: no manual at all, only the models phase (see createFolder).
  folder: boolean;
};

export type PhaseSection = {
  // Section anchor and tab id. Kept as the historical notice-step-N strings: they are
  // in the address bar and in PhaseNav's pairing with usePhaseProgress, and renaming
  // them would say nothing the order does not already say.
  id: string;
  // i18n key for the phase card and the section heading. The NUMBER is not in here —
  // the viewer derives it from position, so reordering renumbers by itself.
  labelKey: string;
  // i18n key for what this phase does, shown in the "i" beside its title.
  //
  // A KEY BESIDE THE LABEL, and not an index into a list. This was a position in a table
  // of notes, and every one of the ways that could go wrong went wrong: phases were
  // reordered and the tooltips followed the old positions, the copy carried a "PHASE 4:"
  // prefix that the heading had long since renumbered to 4, a phase split in two left the
  // half without an index with no tooltip at all, and the three phases added since simply
  // had none. Copy that lives next to the label cannot drift from it, and a phase with no
  // tooltip is now a missing line in this file rather than an off-by-one nobody can see.
  infoKey?: string;
  // WHETHER THE CHOSEN SUBMODEL CHANGES ANYTHING ON THIS PHASE — the sidebar's picker is greyed
  // where it does not (see SubmodelPicker). Only the parts, the materials and the meshes read
  // it; the manual, the steps, the assembly and the publication are one product whatever the
  // colourway.
  submodel?: boolean;
  render: (ctx: PhaseCtx) => ReactNode;
};

// Reading the manual: classify its pages, list the parts it supplies, break its
// assembly into steps and sub-steps. Both pipelines do all of this identically — it is
// what the manual SAYS, and that does not depend on how the result will be shown.
//
// PARTS is one phase with two views (see PartsPhase): the same parts table wherever it is
// read from, and the meshes beside it. What those parts are made of follows at once — it reads
// the parts and nothing after them. The steps are one phase in two halves — how the
// supplied parts are shared out among them, and then what each step is made of, in that order
// because the analysis is handed the repartition. The hardware table read off them is the
// models phase's (see ModelsPhase): the hardware is built from its rows.
function readManual(): PhaseSection[] {
  return [
    // THE MANUAL AND WHAT IT SAYS, in one phase, with the listings beside it: two
    // subphases, one per source, each holding its inputs (the PDF, the links) with the
    // reads of them. The upload was
    // a phase of its own with nothing to run in it, and its inputs sat a tab away from the
    // reads that use them — a Sections cell sent you to a PDF on another phase.
    //
    // `notice-top` was that phase's tab id; the viewer still reads it, from a bookmark or a
    // remembered position, as this one.
    {
      id: 'notice-step-1',
      labelKey: 'notice.step1.title',
      infoKey: 'notice.phase.info.sections',
      render: (c) => (
        <CategoryTable
          numPages={c.numPages} docId={c.docId} file={c.file} locked={c.locked} manual={c.manual}
        />
      ),
    },
    {
      id: 'notice-step-2',
      labelKey: 'notice.parts.phaseTitle',
      submodel: true,
      infoKey: 'notice.phase.info.partsModels',
      render: (c) => (
        <PartsPhase file={c.file} docId={c.docId} locked={c.locked} held={c.held} models />
      ),
    },
    // WHAT THE PRODUCT IS MADE OF, as a phase of its own: read once per colourway, with the
    // hardware's hand-set materials beside it, and every mesh is then built with that answer
    // rather than judging its own materials on the way past. It was the first card of the
    // models phase; see ModelsPhase.
    //
    // RIGHT AFTER THE PARTS, because the parts are all it reads: their list, their categories,
    // and the listing crops and outlines Phase 2 found for them. Nothing from the steps. It
    // sat after them, just ahead of the meshes it paints, and a phase that waited on two phases
    // it never reads had a number that predicted nothing.
    //
    // A NEW ID, and the meshes keep theirs: links out of the Parts DB and bookmarks name
    // `notice-step-7` meaning the meshes, and they still land there.
    {
      id: 'notice-materials',
      labelKey: 'notice.3d.subMaterials',
      // EVERY COLOURWAY ON THE PAGE AT ONCE (see MaterialsPanel), so the picker says nothing here.
      infoKey: 'notice.phase.info.materials',
      render: (c) => <MaterialsPhase docId={c.docId} file={c.file} locked={c.locked} />,
    },
    // THE STEPS, in one phase: the overview (the parts and tools shared out among the steps,
    // what each does and prints — one reader holding every step, since a quantity supplied is
    // a quantity shared) and then the sub-steps (each step broken into its gestures, one call
    // per step, given the overview). They were two phases; one badge runs and shows both.
    //
    // The tab ids do NOT follow, and are not meant to. They are in the address bar, they were
    // always historical (see PhaseSection.id): the overview's id is this phase's, and the one
    // the sub-steps had is the hardware table's below.
    {
      id: 'notice-step-4',
      labelKey: 'notice.merged.title',
      infoKey: 'notice.phase.info.steps',
      render: (c) => <StepsSubphases docId={c.docId} file={c.file} locked={c.locked} held={c.held} />,
    },
  ];
}

// The reader, in order. It reads the manual, photographs the supplied parts and
// reconstructs a mesh for each, and publishes what it has: a public tutorial page and its
// QR, a screen per sub-step, with the placeholder frame the public page draws where a step
// has nothing to show.
//
// The assembly render that will fill those frames does not exist yet. The page, the QR and
// the step-by-step sequence do, and they are the half of this that never depended on it.
export const PIPELINE: PhaseSection[] = [
  ...readManual(),
  // THE MESHES, on their own, after everything the manual SAYS has been read.
  //
  // They were the tail of Phase 2, under the same part selector as the table — the part, its
  // pictures, and the thing reconstructed from them, in the order the work happens. Which
  // reads well and is a phase too early. A mesh is not made out of the manual's words: it is
  // made out of pictures, and what it needs to be RIGHT — which holes a part has, and where —
  // is decided in Phase 4, where the gestures are broken down and every hole is filled by a
  // piece of hardware in one of them. A model built in Phase 2 is built before anything in
  // this app knows a hole exists.
  //
  // The same page either way: one component, two views (see PartsPhase). The selector, the
  // submodel filter and which part is open are shared, because a second copy of them is two
  // selectors that disagree about which part you are looking at.
  {
    id: MODELS_TAB,
    labelKey: 'notice.models.phaseTitle',
    submodel: true,
    infoKey: 'notice.phase.info.partModels',
    render: (c) => (
      <ModelsPhase file={c.file} docId={c.docId} locked={c.locked} held={c.held} folder={c.folder} />
    ),
  },
  // THE ASSEMBLY: what the manual's parts DO, after four phases of what the manual says.
  //
  // Between the repartition and the publication, and it has to be: it works on parts that
  // Phase 3 has already shared out among the steps, and the page Phase 6 publishes is where
  // its result will eventually be drawn. `notice-step-5` was the one id in this file's
  // historical run that nothing had taken, and it happens to land exactly in the gap.
  //
  // It is the first phase that is not a reading of the whole manual. Everything before it
  // asks a model a question about a PDF and answers it for every step at once; this one asks
  // about ONE sub-step, when its button is pressed, and then asks a person to say how the two
  // parts actually meet. Which is why it still takes no part in run-all: a run-all that
  // answered every sub-step would spend a call on each to fill selectors nobody is looking at.
  {
    id: 'notice-step-5',
    labelKey: 'notice.assembly.title',
    infoKey: 'notice.phase.info.assembly',
    render: (c) => <AssemblySubphases docId={c.docId} />,
  },
  {
    id: 'notice-step-6',
    labelKey: 'notice.step7.title',
    infoKey: 'notice.phase.info.publishModels',
    render: (c) => (
      <PublishStep docId={c.docId} file={c.file} name={c.name} active={c.active} />
    ),
  },
];

// THE NUMBER A PHASE WEARS, and it is its POSITION — the number on its badge, the number in
// the address, and the number somebody says out loud. Nothing stores it: the ids below are
// the historical `notice-step-N` strings and they have not matched the order for a long time
// (Phase 8, Publish, is `notice-step-6`), which is exactly why the number is derived from
// where a phase SITS rather than read off what it is called. Reordering renumbers by itself.
//
// The manual is not a phase of its own any more: it is in Phase 1's Manual subphase, so a
// section's number is its index plus one.

export const FIRST_TAB = PIPELINE[0].id;

// TABS THAT ARE NOT PHASES ANY MORE, still honoured wherever an old id can arrive from — a
// bookmark, a remembered position: the manual's, which is in Phase 1 now, and the hardware
// table's, which is a subphase of the models phase.
const LEGACY_TABS: Record<string, string> = {
  'notice-top': FIRST_TAB,
  'notice-step-3': 'notice-step-7',
};
export const currentTab = (tab: string): string => LEGACY_TABS[tab] ?? tab;

// 0 for a tab this pipeline does not have, which every caller already has to handle: an id
// out of a bookmark, or out of a pipeline that has since changed.
export const phaseNumberOf = (tab: string): number => {
  const at = PIPELINE.findIndex((p) => p.id === currentTab(tab));
  return at < 0 ? 0 : at + 1;
};

// And back — null where the number names no phase, so a hand-typed ?phase=99 falls through
// to wherever the reader would have opened anyway rather than onto a blank tab.
export const phaseTabAt = (n: number): string | null => PIPELINE[n - 1]?.id ?? null;
